/**
 * A metric's figure from a report's sums (docs/analytics.md §5: the backend returns sums; rates and
 * averages are worked out here, and only here), and how that figure is written.
 */

import type { LiveEventRow, Totals } from "./api";
import type { MetricId } from "./definitions";
import { change, count, duration, percent, rate, shareChange, type Change } from "./format";

/** The figure for a metric the summary's totals hold; null for a rate of nothing. */
export function totalsFigure(id: MetricId, t: Totals): number | null {
  switch (id) {
    case "plays": return t.plays;
    case "engagedPlays":
    case "recipesWatched": return t.engagedPlays;
    case "watchSeconds": return t.watchSeconds;
    case "viewers": return t.viewers;
    case "completed": return t.completed;
    case "averageWatch": return rate(t.watchSeconds, t.plays);
    case "engagedRate": return rate(t.engagedPlays, t.plays);
    case "completionRate": return rate(t.completed, t.plays);
    default: return null;
  }
}

/** Live headlines over a range's events: everyone who watched live, or the highest peak. */
export function liveFigure(key: "liveViewers" | "peak", events: readonly LiveEventRow[]): number | null {
  if (!events.length) return null;
  return key === "peak" ? Math.max(...events.map((e) => e.peak)) : events.reduce((sum, e) => sum + e.liveViewers, 0);
}

const DURATIONS = new Set<MetricId>(["watchSeconds", "averageWatch", "liveWatch", "replayWatch"]);
const SHARES = new Set<MetricId>(["engagedRate", "completionRate", "retention"]);

/** A figure as its metric is written: a duration, a percentage or a count. */
export function formatFigure(id: MetricId, value: number | null | undefined): string {
  if (DURATIONS.has(id)) return duration(value);
  if (SHARES.has(id)) return percent(value);
  return count(value);
}

/** The change in a metric against the previous period: shares in points, durations as durations. */
export function changeOf(id: MetricId, current: number | null, previous: number | null): Change {
  if (SHARES.has(id)) return shareChange(current, previous);
  return change(current, previous, DURATIONS.has(id) ? duration : count);
}
